import React, { useState, useMemo, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import LoadingSpinner from '@/components/tailwind/LoadingSpinner';
import { Breakpoints, SHOW_LOADER_DELAY } from '@/settings';
import { Provider, useSelector, useStore } from 'react-redux';
import { useAppDispatch } from '@/store';
import Cookies from 'js-cookie';
import * as Actions from '@/actions/pdp.actions';
import { PDPViews } from '@types';
import ScrollTo from '@/components/tailwind/ScrollTo';

// @ts-ignore
import { Designer } from '@c2s-npm/plumreact';
import axios from 'axios';
import BundleHeader from './BundleHeader';
import ItemInfo from './BundleInfo';
import useAsset from '@features/partner/hooks/useAsset';
import ConfirmModal, { ConfirmModalRef } from '@features/pdp/designer/BundleInfo/ConfirmModal';
import { useMediaQuery } from 'react-responsive';
import classNames from 'classnames';
import equal from 'fast-deep-equal';

const DesignerTitle = ({ partnerConfig }) => {
  return (
    <>
      {!!partnerConfig?.partner && (
        <div className='tw-order-1 tw-mb-5'>
          {partnerConfig?.partner?.logoUrl && (
            <img
              src={partnerConfig.partner.logoUrl}
              className='tw-h-full tw-max-h-[48px] tw-w-auto tw-max-w-full'
            />
          )}
        </div>
      )}
    </>
  );
};

const defaultDesign = {
  faces: [],
};

export const BundleDesigner = ({ defaultFace = 'empty', bundleInfo, onChoiceChange }) => {
  const dispatch = useAppDispatch();
  const wrapperRef = useRef(null);
  const designerRef = useRef(null);
  const confirmModalRef = useRef<ConfirmModalRef>(null);
  const pdpItem = useSelector((state: any) => state.pdpItem);
  const { details, miyo = {} } = pdpItem;

  const bundleItem = bundleInfo.selectedItems[bundleInfo.customInfo.customizing];
  const isInline = bundleInfo.bundle.isInline;

  useEffect(() => {
    if (!bundleItem) {
      bundleInfo.customInfo.setIsCustomizing(false);
    }
  }, [!bundleItem]);

  const { itemId } = bundleItem;

  const {
    designId,
    partnerId: partnerIdQueryParam,
    edit: editQueryParam,
    copy: copyQueryParam,
    debug: debugQueryParam,
    export: exportQueryParam,
    secret: secretQueryParam,
  } = Object.fromEntries(new URLSearchParams(window.location.search).entries());
  const debug = (debugQueryParam ?? Cookies.get('miyoDebug')) === 'true';

  const withSecret = (prefix) => (secretQueryParam ? `${prefix}&secret=${secretQueryParam}` : '');

  const [config, setConfig] = useState(null);
  const [uploads, setUploads] = useState(null);
  const [library, setLibrary] = useState(null);
  const [loading, setLoading] = useState(null);
  const [exportStatus, setExportStatus] = useState(null);
  // don't remove, responsible for loading default design with asset
  useAsset();

  // convert hashed urls back to paths so user can edit designs (definitely a kludge)
  const _design =
    bundleInfo.customInfo.designs[bundleInfo.customInfo.customizing]?.designData || defaultDesign;
  const design = useMemo(() => {
    return !!uploads
      ? {
          ..._design,
          faces: _design.faces.map((face) => ({
            ...face,
            elements: face.elements.map((element) => ({
              ...element,
              url: uploads.find((u) => u.id === element.fileId)?.url,
            })),
          })),
        }
      : _design;
  }, [_design, uploads]);

  const setDesign = useCallback(
    (d) => {
      const customizing = bundleInfo.customInfo.customizing;
      bundleInfo.customInfo.updateDesign(customizing, {
        designData: d,
        config,
      });
      if (design == defaultDesign && customizing !== 0) {
        onChoiceChange(customizing, d.itemId);
      }
    },
    [config]
  );

  const { partnerConfig = {} } = miyo || {};
  const partnerId = partnerConfig?.partner?.id;
  const limitAssets = !!partnerConfig?.partner?.limitAssets;
  const assetId = miyo?.asset?.id;

  const miyoFee = miyo?.faceFee?.rate || 0;

  const isSmallCanvas = Math.min(...(config || []).map((c) => c.print.height)) <= 12;

  const canArtwork = !isSmallCanvas;
  const canMonogram = !isSmallCanvas;
  const canJersey = !isSmallCanvas;
  const canUpload = !isSmallCanvas && !limitAssets;

  // TODO drive based on cart?
  const quantityRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      if (itemId) {
        const { data } = await axios.get(`/custom/config/${itemId}`);
        setConfig(data);
      } else {
        setConfig(false);
      }
    })();
  }, [itemId]);

  useEffect(() => {
    (async () => {
      setLoading('Loading asset library...');
      const { data } = await axios.get(
        '/custom/assets?' +
          (debug ? '&debug=true' : '') +
          (partnerIdQueryParam ? '&partnerId=' + partnerIdQueryParam : '')
      );
      setLibrary(data);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!config) return;
    (async () => {
      if (!designId) {
        setLoading('Loading user design library...');
        const { data } = await axios.get('/custom/designs');
        setUploads(data);
        setLoading(false);
      } else {
        setLoading('Loading design...');
        const { data } = await axios.get(`/custom/design/${designId}` + withSecret('?'));

        const withUrls = Object.assign(data, {
          faces: data.faces.map((f) =>
            Object.assign(f, {
              elements: f.elements.map((e) =>
                e.hash
                  ? Object.assign(e, {
                      url: `/custom/designs/${e.hash}` + withSecret('?'),
                    })
                  : e
              ),
            })
          ),
        });

        if (copyQueryParam) {
          delete withUrls.id;
        } else if (editQueryParam) {
          withUrls.copyId = withUrls.id;
          delete withUrls.id;
        }

        setDesign(withUrls);
        setLoading(false);
      }
    })();
  }, [config]);

  const processing = !library || !config;

  const handleUpload = (e) => {
    e && setUploads([e].concat(uploads || []));
  };

  const handleSubmit = async (e) => {
    setLoading(true);

    try {
      const hash = (s) =>
        `${s.split('').reduce((a, b) => {
          a = (a << 5) - a + b.charCodeAt(0);
          return a & a;
        }, 0)}`;

      const toFile = async (base64, filename) => {
        const split = base64.split(',');
        //const data = split[0].indexOf('base64') >= 0 ? atob(split[1]) : decodeURI(split[1]);
        const mime = split[0].split(':')[1].split(';')[0];

        //const encoder = new TextEncoder();
        // const bytes = encoder.encode(data);
        const buffer = await (await fetch(base64)).arrayBuffer();
        const bytes = [buffer]; // new Uint8Array(buffer);

        return new File(bytes, filename, { type: mime });
      };

      const { faceProductPreviews, faces, files } = e.faces.reduce(
        ({ faceProductPreviews, faces, files }, face) => {
          const hashedProductPreview = face.face + '.' + hash(face.productPreview);
          const hashedPreview = face.face + '.' + hash(face.preview);
          // const hashedRender = face.face + '.' + hash(face.render);

          const { elements, files: elementFiles } = face.elements.reduce(
            ({ elements, files }, element) => {
              if (element.elementType === 'uploaded' || element.elementType === 'file') {
                const hashedUrl = hash(element.url);
                return {
                  elements: elements.concat([Object.assign({}, element, { url: hashedUrl })]),
                  files: files.concat([{ hash: hashedUrl, url: element.url }]),
                };
              } else {
                return {
                  elements: elements.concat([element]),
                  files,
                };
              }
            },
            { elements: [], files }
          );

          const hashedFace = Object.assign({}, face, {
            elements,
            productPreview: hashedProductPreview,
            preview: hashedPreview,
            // render: hashedRender,
          });
          delete hashedFace.render;
          return {
            faceProductPreviews: faceProductPreviews.concat({
              face: face.face,
              url: face.productPreview,
            }),
            faces: faces.concat([hashedFace]),
            files: files.concat(elementFiles).concat([
              { hash: hashedProductPreview, url: face.productPreview, base64: true },
              { hash: hashedPreview, url: face.preview, base64: true },
              // engraved face.render is a DXF file as string, color is a PNG data url
              // { hash: hashedRender, url: face.render, base64: face.render?.startsWith('data:') },
            ]),
          };
        },
        { faceProductPreviews: [], faces: [], files: [] }
      );

      const previewUrl = e.preview;
      if (e.preview) {
        files.push({
          hash: 'preview.product.all',
          url: e.preview,
          base64: true,
        });
        delete e.preview;
      }

      const formData = new FormData();
      await Promise.all(
        files.map(async ({ hash, url, base64 }) => {
          const file = base64 ? await toFile(url, hash) : new File([url], hash, { type: 'image/x-dxf' });
          formData.append('file', file);
        })
      );

      const getFee = (asset) => {
        if (library?.fees && asset?.feeId) {
          return library.fees.find((f) => f.id === asset.feeId);
        } else {
          return null;
        }
      };

      const withFee = (a, o) => {
        const fee = getFee(a);
        return fee ? Object.assign(o || {}, { fee }) : o;
      };
      const allAssets = library?.assets || [];
      const facesForPricing = faces.map((f) => ({
        ...f,
        elements: f.elements.map((element) => {
          const asset = allAssets.find((a) => a.id === element.assetId);
          return withFee(asset, element);
        }),
      }));

      const quantity = 1;
      const designData = Object.assign(e, {
        shopItemId: bundleItem.id,
        faces: facesForPricing,
        id: design?.id,
        partnerId,
        quantity,
        userAgent: window?.navigator?.userAgent,
      });
      formData.append('design', JSON.stringify(designData));
      if (design?.copyId) {
        formData.append('copyId', design.copyId);
      }

      if (designData?.faces?.length == 0) {
        bundleInfo.customInfo.updateDesign(bundleInfo.customInfo.customizing, null);
      } else {
        bundleInfo.customInfo.updateDesign(bundleInfo.customInfo.customizing, {
          designData,
          formData,
          previewUrl,
          faceProductPreviews,
          config,
        });
      }
      if (editQueryParam) {
        await axios.post('/cart/custom', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        window.location = jsRoutes.controllers.Cart.view().url;
      } else {
        bundleInfo.customInfo.setIsCustomizing(false);
      }
    } catch (e) {
      console.error(e);
      alert('There was a problem saving your design.');
      setExportStatus('error');
      setLoading(false);
    }
  };

  const handleInit = () => {
    if (exportQueryParam) {
      designerRef.current.submit();
    }
  };

  const handleBack = () => {
    // hack!!!
    const faces = designerRef.current.getDesign();
    const oldDesign = { itemId: design.itemId, faces: design.faces };
    const newDesign = { itemId, faces };
    const isDirty = !equal(oldDesign, newDesign) && faces.length > 0;
    const isEmpty = !faces.find((f) => f.elements.length > 0);
    const isOldEmpty = !oldDesign.faces.find((f) => f.elements.length > 0);

    if (isDirty && !(isEmpty && isOldEmpty)) {
      confirmModalRef.current.openModal();
    } else {
      if (!!editQueryParam) {
        window.location = jsRoutes.controllers.Cart.view().url;
      } else {
        bundleInfo.customInfo.setIsCustomizing(false);
        dispatch(Actions.setView(PDPViews.DEFAULT));
      }
    }
  };
  const onExitReject = () => {
    confirmModalRef.current.closeModal();
    !!design.itemId && onChoiceChange(bundleInfo.customInfo.customizing, design.itemId);
    bundleInfo.customInfo.setIsCustomizing(false);
    dispatch(Actions.setView(PDPViews.DEFAULT));
  };

  const onExitConfirm = () => {
    const faces = designerRef.current.getDesign();
    const isEmpty = design === defaultDesign || !faces.find((f) => f.elements.length > 0);
    if (isEmpty) {
      if (!!editQueryParam) {
        window.location = jsRoutes.controllers.Cart.view().url;
      } else {
        handleSubmit(defaultDesign);
      }
    } else {
      confirmModalRef.current.closeModal();
      designerRef.current.setView('review');
    }
  };

  const itemInfoRef = useRef(null);
  const pdpStore = useStore();

  const ItemInfoComponent = useCallback(
    (p) => (
      <Provider store={pdpStore}>
        <ItemInfo
          ref={itemInfoRef}
          {...{ bundleInfo, onChoiceChange }}
          {...p}
          hidePrice={!isInline}
          action={design === defaultDesign ? 'add' : 'update'}
        />
      </Provider>
    ),
    [itemInfoRef, bundleInfo, onChoiceChange]
  );

  const DesignerHeaderComponent = useCallback(
    (p) => (
      <Provider store={pdpStore}>
        <BundleHeader {...p} {...{ bundleInfo, onChoiceChange, handleBack }} />
      </Provider>
    ),
    [bundleInfo, onChoiceChange, handleBack]
  );

  const designerItem = {
    displayNameOnPage: details?.displayNameOnPage || bundleInfo?.bundle?.displayName,
    url: (details || bundleInfo?.bundle)?.url,
    miyoFee,
    typeId: details?.attributes?.typeId || bundleItem?.attributes?.type,
  };

  const TitleComponent = useCallback((p) => <DesignerTitle {...p} {...{ partnerConfig }} />, [partnerConfig]);

  const isMobile = useMediaQuery({ maxWidth: Breakpoints.LG });
  const fullscreen = useMemo(() => isMobile && config?.length > 0, [isMobile, config]);
  const graphicTypes = useMemo(() => {
    return [
      ...new Set(
        (config || []).flatMap((c) => {
          return Object.keys(c.miyoConfig?.graphicType || {}).filter(
            (key) =>
              c.miyoConfig.graphicType[key] !== 'disabled' &&
              (window.DD_ENV === 'dev' || c.miyoConfig.graphicType[key] === 'prod')
          );
        })
      ),
    ];
  }, [config]);

  useLayoutEffect(() => {
    const timeoutId = setTimeout(() => {
      const hasNoScroll = document.querySelector('#main').classList.contains('miyo-no-scroll');
      if (fullscreen) {
        if (!hasNoScroll) {
          document.body.scrollTop = 0;
          document.querySelector('#main').classList.add('miyo-no-scroll');
          document.documentElement.classList.add('miyo-no-scroll');
        }
      } else {
        if (hasNoScroll) {
          document.body.scrollTop = 0;
          document.querySelector('#main').classList.remove('miyo-no-scroll');
          document.documentElement.classList.remove('miyo-no-scroll');
        }
      }
    }, 300);

    return () => {
      console.log('unmounting designer');
      if (timeoutId) clearTimeout(timeoutId);
      const hasNoScroll = document.querySelector('#main').classList.contains('miyo-no-scroll');
      if (hasNoScroll) {
        document.body.scrollTop = 0;
        document.querySelector('#main').classList.remove('miyo-no-scroll');
        document.documentElement.classList.remove('miyo-no-scroll');
      }
    };
  }, [fullscreen]);

  return (
    <div id='miyo' className='tw-relative' ref={wrapperRef}>
      <ScrollTo />
      {loading && <LoadingSpinner title={loading} />}

      {config && config.length > 0 && (
        <Designer
          ref={designerRef}
          contentClassName='tw-mx-auto tw-max-w-[1456px] tw-px-4'
          {...{
            config,
            design,
            graphicTypes,
            library,
            processing,
            itemInfoComponent: ItemInfoComponent,
            getTotalPrice: itemInfoRef.current?.getTotalPrice,
            designerHeaderComponent: DesignerHeaderComponent,
            titleComponent: TitleComponent,
            onSubmit: handleSubmit,
            onUpload: handleUpload,
            onInit: handleInit,
            onBack: handleBack,
            previewUrl: '/custom/preview',
            uploadUrl: '/custom/designs',
            uploaded: uploads,
            withUpload: canUpload,
            withMonogram: canMonogram,
            withJersey: canJersey,
            withArtwork: canArtwork,
            withGroup: false,
            debug,

            item: designerItem,
          }}
        />
      )}
      {config && config.length === 0 && (
        <div className='display-wrapper'>
          <em>Product not customizable</em>
        </div>
      )}
      <ConfirmModal
        ref={confirmModalRef}
        onConfirm={onExitConfirm}
        onReject={onExitReject}
        title={'Before you go...'}
        confirmText={'Review Changes'}
        rejectText={'Revert Changes'}>
        <p>
          Looks like you've made a few changes to your design. Would you like to review your changes before
          leaving, or revert them to the previous version?
        </p>
      </ConfirmModal>
    </div>
  );
};

export default BundleDesigner;
